"use client"

import {
  useArtifactCanvas,
  ListFilterBar,
  PaneListCount,
  PaneResponsiveCardGrid,
  CV_STATUS_FILTERS,
  cvStatusFilterCounts,
  formatCvStatusPillLabel,
  applyTalentBrowseFiltersToSearchParams,
  buildCityFacets,
  buildCountryFacets,
  citySelectionKey,
  DEFAULT_TALENT_BROWSE_FILTERS,
  filterTalentPool,
  normalizeCountryKey,
  parseTalentBrowseFiltersFromSearchParams,
  buildSkillFacets,
  buildCompanyFacets,
  activeLocationCountries,
  getRecruiterTalentFilterDefinitions,
  recruiterCityFilterChange,
  recruiterCountryFilterChange,
  talentDisplayName,
  DashboardSectionAction,
  Button,
  Card,
  CardContent,
  useCVDocuments,
} from '@yeshuman/sdk'
import type { TalentBrowseFilters, CVDocument } from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ChartNoAxesColumn, Users } from 'lucide-react'

import { CandidateCardSkeleton } from './candidate-card-skeleton'
import { TalentProfileCard } from './talent-profile-card'
import { TalentProfilePreviewCanvas } from './talent-profile-preview-canvas'

export type RecruiterTalentBrowseSectionProps = {
  onUploadClick?: () => void
}

export function RecruiterTalentBrowseSection({ onUploadClick }: RecruiterTalentBrowseSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const { cvDocuments, isLoading, error, refetch } = useCVDocuments()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const previewOpenKeyRef = useRef(0)

  const filters = useMemo(
    () => parseTalentBrowseFiltersFromSearchParams(searchParams),
    [searchParams],
  )

  const [textDraft, setTextDraft] = useState(filters.textQuery)

  useEffect(() => {
    setTextDraft(filters.textQuery)
  }, [filters.textQuery])

  const updateFilters = useCallback(
    (next: TalentBrowseFilters) => {
      const base = new URLSearchParams(searchParams)
      base.delete('tab')
      setSearchParams(applyTalentBrowseFiltersToSearchParams(base, next), { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const filterCounts = useMemo(() => cvStatusFilterCounts(cvDocuments), [cvDocuments])

  const visibleStatusFilters = useMemo(
    () =>
      CV_STATUS_FILTERS.filter(({ value }) => {
        if (value === 'all') return true
        return filterCounts[value] > 0
      }),
    [filterCounts],
  )

  const skillFacets = useMemo(() => buildSkillFacets(cvDocuments), [cvDocuments])
  const countryFacets = useMemo(() => buildCountryFacets(cvDocuments), [cvDocuments])
  const scopedCountries = useMemo(() => activeLocationCountries(filters), [filters])
  const cityFacets = useMemo(
    () => buildCityFacets(cvDocuments, scopedCountries),
    [cvDocuments, scopedCountries],
  )
  const companyFacets = useMemo(() => buildCompanyFacets(cvDocuments), [cvDocuments])
  const filterDefinitions = useMemo(() => getRecruiterTalentFilterDefinitions(), [])

  const countryFilterOptions = useMemo(
    () =>
      countryFacets.map((facet) => ({
        value: facet.key,
        label: `${facet.label} (${facet.count})`,
      })),
    [countryFacets],
  )

  const cityFilterOptions = useMemo(
    () =>
      cityFacets.map((facet) => ({
        value: facet.key,
        label: `${facet.label} (${facet.count})`,
      })),
    [cityFacets],
  )

  const selectedCountryKeys = useMemo(
    () => filters.selectedCountries.map(normalizeCountryKey),
    [filters.selectedCountries],
  )

  const selectedCityKeys = useMemo(
    () => filters.selectedCities.map((selection) => citySelectionKey(selection.city, selection.country)),
    [filters.selectedCities],
  )

  const filteredDocuments = useMemo(
    () => filterTalentPool(cvDocuments, filters),
    [cvDocuments, filters],
  )

  useEffect(() => {
    if (filters.statusFilter !== 'all' && filterCounts[filters.statusFilter] === 0) {
      updateFilters({ ...filters, statusFilter: 'all' })
    }
  }, [filterCounts, filters, updateFilters])

  const openProfilePreview = useCallback(
    (document: CVDocument) => {
      const profileId = document.parsed_profile_id
      if (!profileId || document.parsing_status !== 'completed') return

      previewOpenKeyRef.current += 1
      const displayName = talentDisplayName(document)
      openCanvas({
        title: displayName,
        preventAutoFocus: true,
        content: (
          <TalentProfilePreviewCanvas
            key={`talent-profile-${profileId}-${previewOpenKeyRef.current}`}
            profileId={profileId}
            profileName={displayName}
            onClose={closeCanvas}
          />
        ),
      })
    },
    [closeCanvas, openCanvas],
  )

  const handleCountryFilterChange = useCallback(
    (keys: string[]) => {
      updateFilters(recruiterCountryFilterChange(filters, keys, countryFacets))
    },
    [countryFacets, filters, updateFilters],
  )

  const handleCityFilterChange = useCallback(
    (keys: string[]) => {
      updateFilters(recruiterCityFilterChange(filters, keys))
    },
    [filters, updateFilters],
  )

  const commitTextQuery = useCallback(
    (value: string) => {
      if (value.trim() === filters.textQuery.trim()) return
      updateFilters({ ...filters, textQuery: value.trim() })
    },
    [filters, updateFilters],
  )

  const clearAllFilters = useCallback(() => {
    updateFilters(DEFAULT_TALENT_BROWSE_FILTERS)
    setTextDraft('')
  }, [updateFilters])

  const runSmartSearchWithSkills = useCallback(() => {
    if (filters.selectedSkills.length === 0) return
    const next = applyTalentBrowseFiltersToSearchParams(new URLSearchParams(), filters)
    next.set('tab', 'search')
    setSearchParams(next, { replace: true })
  }, [filters, setSearchParams])

  const showFilters = !isLoading && !error && cvDocuments.length > 0

  const filterCtx = useMemo(
    () => ({
      skillFacets,
      countryFacets,
      cityFacets,
      companyFacets,
      countryFilterOptions,
      cityFilterOptions,
      selectedCountryKeys,
      selectedCityKeys,
      onCountryFilterChange: handleCountryFilterChange,
      onCityFilterChange: handleCityFilterChange,
      showFilters,
    }),
    [
      skillFacets,
      countryFacets,
      cityFacets,
      companyFacets,
      countryFilterOptions,
      cityFilterOptions,
      selectedCountryKeys,
      selectedCityKeys,
      handleCountryFilterChange,
      handleCityFilterChange,
      showFilters,
    ],
  )

  const hasActiveFilters = useMemo(
    () =>
      textDraft.trim().length > 0 ||
      filters.statusFilter !== 'all' ||
      filterDefinitions.some((def) => def.isNonDefault(filters, filterCtx)),
    [textDraft, filters, filterDefinitions, filterCtx],
  )

  const statusPills = showFilters ? (
      <div className="flex flex-wrap gap-2">
        {visibleStatusFilters.map(({ value, label }) => (
          <Button
            key={value}
            type="button"
            size="segmented"
            variant={filters.statusFilter === value ? 'secondary' : 'ghost'}
            title={value === 'completed' ? 'CV parsed and previewable' : undefined}
            onClick={() => updateFilters({ ...filters, statusFilter: value })}
          >
            {formatCvStatusPillLabel(label, filterCounts[value])}
          </Button>
        ))}
      </div>
    ) : null

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Browse is a deterministic directory; use Analysis for ranked LLM results.
      </p>

      {showFilters ? (
        <>
          <ListFilterBar
            listId="recruiterTalent"
            filters={filters}
            onFiltersChange={updateFilters}
            definitions={filterDefinitions}
            ctx={filterCtx}
            betweenSlot={statusPills}
            hasActiveFilters={hasActiveFilters}
            onClearAll={clearAllFilters}
            search={{
              searchId: 'talent-browse-search',
              searchPlaceholder: 'Filter by name, role…',
              searchValue: textDraft,
              onSearchChange: setTextDraft,
              onSearchBlur: commitTextQuery,
            }}
          />

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <PaneListCount
                count={filteredDocuments.length}
                unit={{ singular: 'profile', plural: 'profiles' }}
              />
              {hasActiveFilters ? (
                <Button type="button" variant="ghostMuted" size="sm" onClick={clearAllFilters}>
                  Clear all filters
                </Button>
              ) : null}
            </div>
            {filters.selectedSkills.length > 0 ? (
              <div className="self-start sm:self-auto">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={runSmartSearchWithSkills}
                >
                  <ChartNoAxesColumn aria-hidden />
                  Run analysis with {filters.selectedSkills.length}{' '}
                  {filters.selectedSkills.length === 1 ? 'skill' : 'skills'}
                </Button>
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      {isLoading ? (
        <PaneResponsiveCardGrid aria-busy="true" aria-label="Loading talent">
          {Array.from({ length: 4 }).map((_, index) => (
            <CandidateCardSkeleton key={index} />
          ))}
        </PaneResponsiveCardGrid>
      ) : error ? (
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-destructive">
              Error loading talent: {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </CardContent>
        </Card>
      ) : cvDocuments.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
          <Users className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
          <p className="text-sm font-medium">No talent yet</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Upload files to build searchable profiles with parsed skills and experience.
          </p>
          {onUploadClick ? (
            <div className="mt-4">
              <DashboardSectionAction onClick={onUploadClick}>Upload files</DashboardSectionAction>
            </div>
          ) : null}
        </div>
      ) : filteredDocuments.length === 0 ? (
        <Card>
          <CardContent className="space-y-3 py-10 text-center">
            <p className="text-sm font-medium text-muted-foreground">
              {filters.openToWorkOnly
                ? 'No one has marked themselves open to work yet.'
                : 'No profiles match your filters'}
            </p>
            {hasActiveFilters ? (
              <Button type="button" variant="outline" size="sm" onClick={clearAllFilters}>
                Clear all filters
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <PaneResponsiveCardGrid>
          {filteredDocuments.map((document) => (
            <TalentProfileCard
              key={document.id}
              document={document}
              onOpen={() => openProfilePreview(document)}
              onDeleted={refetch}
            />
          ))}
        </PaneResponsiveCardGrid>
      )}
    </div>
  )
}
